import {BACKEND_URL} from '../config';
import DetailedError from './Error';
import {generalErrorMessage, isAbortControllerAvailable, isAbortError} from '../utils/utils';
import {getGlobalAuthService} from '../auth/AuthContextProvider';

const URL = BACKEND_URL;

export enum AbortCategory {
    SEARCH,
    PREVIEW,
    SUGGEST,
    CONFIG,
    FACETS,
    DETAILS,
    TRANSLATIONS,
    SAVED_SEARCH,
    MATA_INFO_OPTIONS,
}

const abortControllerMap = new Map<AbortCategory, AbortController>();

async function getHeaders(noAuth?: boolean) {
    const headers: Record<string, string> = {
        'Access-Control-Allow-Origin': '*'
    };

    if (!noAuth) {
        const globalAuthService = await getGlobalAuthService();
        headers['X-User-Id'] = globalAuthService.getUserId();
        const token = await globalAuthService.getToken();
        if (globalAuthService.isEnabled() && token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
}

const getHeadersForJsonType = async (noAuth?: boolean) => {
    const headers = await getHeaders(noAuth);
    headers['Content-Type'] = 'application/json';
    return headers;
};

export const fetchPdf = async (
    location: string,
    abortCategory?: AbortCategory
): Promise<Blob> => {
    const options = {
        method: 'GET',
        headers: await getHeadersForJsonType(false)
    };
    const init = handleAbortFetch(options, abortCategory);
    return fetch(URL + location, init)
        .then(
            (res) => res.blob(),
            (e) => handleNonServerErrors(e)
        );
};

export const fetchJson = async <T>(
    location: string,
    method = 'GET',
    abortCategory?: AbortCategory,
    noAuth?: boolean
): Promise<T> => {
    const options = {
        method: method,
        headers: await getHeadersForJsonType(noAuth)
    };
    return doFetch(URL + location, options, abortCategory);
};

export const postJsonWithBody = <T>(
    location: string,
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<T> => {
    return fetchWithBody(location, 'POST', body, abortCategory, cache);
};

export const fetchWithBody = async <T>(
    location: string,
    method = 'GET',
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<T> => {
    const cacheToUse: RequestCache = cache ? cache : 'default';
    const options = {
        method: method,
        headers: await getHeadersForJsonType(false),
        cache: cacheToUse,
        body
    };
    return doFetch(URL + location, options, abortCategory);
};

export const deleteWithPathVariable = async <T>(
    location: string,
    pathVariable: number | string,
    abortCategory?: AbortCategory
): Promise<T> => {
    return doFetch(
        URL + location + '/' + pathVariable,
        {
            method: 'DELETE',
            headers: await getHeaders()
        },
        abortCategory
    );
};

/**
 * Performs the actual fetch request.
 * Returns either
 * - a Promise of given generic type
 * - throws a DetailedError, if the response is not ok or
 * - returns undefined, if the request was aborted
 */
function doFetch<T>(url: RequestInfo, options: RequestInit, abortCategory?: AbortCategory): Promise<T> {
    options = handleAbortFetch(options, abortCategory);
    return fetch(url, options).then(
        (r) => extractContentFromResponse(r),
        (e) => handleNonServerErrors(e)
    );
}

/**
 * Extracts the content of a given Response.
 *
 * If the response is ok, the method returns
 * - either a json, if there is a json response,
 * - a text if there is a text response or
 * - undefined if there is neither of those
 *
 * If the response is not ok, the method throws an according DetailedError
 *
 * @param response a response maybe containing content of the given generic response type
 * @return a Promise of the given generic response type
 * @throws DetailedError if the response is not ok
 */
function extractContentFromResponse<T>(response: Response): Promise<T> {
    const hasJsonBody = response.headers.get('content-type')?.startsWith('application/json');
    const extractedContent = (hasJsonBody ? response.json() : response.text()).catch(() => undefined);
    if (response.ok) {
        return extractedContent;
    }
    return extractedContent.then((errorData?: any) => {
        if (errorData) {
            throw new DetailedError({
                errorType: response.status,
                errorMessage: response.statusText,
                errorData,
            });
        }
        switch (response.status) {
            case 401:
                throw new DetailedError({
                    errorType: response.status,
                    errorMessage: 'Invalid credentials',
                    source: 'fetcher@fetchHandler',
                });
            case 403:
                throw new DetailedError({
                    errorType: response.status,
                    errorMessage: 'Forbidden access',
                });
            default:
                throw new DetailedError({
                    errorType: response.status,
                    errorMessage: generalErrorMessage,
                });
        }
    });
}

/**
 * This function handles all errors that are not caused by the services.
 * Examples: Abort errors, service not reachable, etc.
 *
 * If the given error is an abort error, then the function does not throw an error but returns silently with undefined.
 * In all other cases, a DetailedError is thrown.
 *
 * @param e either an abort error or any other error object
 * @return undefined if the given error is an abort error
 * @throws DetailedError if the given error is not an abort error
 */
function handleNonServerErrors<T>(e: Error): Promise<T> {
    if (isAbortError(e)) {
        // @ts-ignore todo: find a better solution than ignoring the empty response
        return;
    }
    throw new DetailedError({
        errorType: 0,
        errorMessage: `${e.name}: ${e.message}`,
        errorData: e.stack,
    });
}

/**
 * Handles aborting a fetch call, if a call is already pending with the same {@link AbortCategory}.
 */
function handleAbortFetch(options: RequestInit, abortCategory: AbortCategory | undefined): RequestInit {
    if (abortCategory === undefined || !isAbortControllerAvailable()) {
        return options;
    }

    checkFetchesToAbort(abortCategory);
    return addAbortController(options, abortCategory);
}

/**
 * Adds a new {@link AbortController} if an {@link AbortCategory} is defined and adds it to the options.
 */
function addAbortController(options: RequestInit, abortCategory: AbortCategory): RequestInit {
    const controller = new AbortController();
    abortControllerMap.set(abortCategory, controller);
    options.signal = controller.signal;
    return options;
}

/**
 * Abort previously made and still in-progress fetches of the same abort category.
 * Additionally, when a search fetch is executed, also abort previous suggest fetches.
 * @param abortCategory abort category of the fetch to execute.
 */
function checkFetchesToAbort(abortCategory: AbortCategory) {
    if (abortCategory === AbortCategory.SEARCH) {
        checkFetchesToAbortInternal(AbortCategory.SUGGEST);
    }
    checkFetchesToAbortInternal(abortCategory);
}

/**
 * Check if a fetch has previously been made. If it is stil in progress and has an {@link AbortCategory},
 * it will be aborted.
 */
function checkFetchesToAbortInternal(abortCategory: AbortCategory) {
    const abortController = abortControllerMap.get(abortCategory);
    if (abortController) {
        abortController.abort();
        abortControllerMap.delete(abortCategory);
    }
}
